import { useState, useEffect, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, useParams, Link, Navigate } from "react-router-dom";
import axios from "axios";
import "@/App.css";
import { articles, getArticleBySlug } from "./blog/articles";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============== AUTH CONTEXT ==============
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(
      `${API}/auth/login`,
      { email, password },
      { withCredentials: true }
    );
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// ============== LOGIN PAGE ==============
const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const formatApiErrorDetail = (detail) => {
    if (detail == null) return "Une erreur est survenue. Veuillez réessayer.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
      return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-3xl">€</span>
            </div>
          </div>

          <h1 className="text-3xl font-bold text-center text-gray-900 mb-2">
            Connexion Admin
          </h1>
          <p className="text-center text-gray-600 mb-8">
            Accédez à votre tableau de bord
          </p>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="votre@email.com"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Mot de passe
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="••••••••"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white font-bold py-3 px-6 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Connexion..." : "Se connecter"}
            </button>
          </form>

          <div className="mt-6 text-center">
            <Link
              to="/"
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              ← Retour à l'accueil
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============== HOME / LANDING PAGE ==============
const Home = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    annual_income: "",
    profile_type: "",
    annual_tax_amount: "",
    needs_urgent_action: false,
    preparing_pension: false
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleNextStep = (e) => {
    e.preventDefault();
    if (step === 1) {
      // Validate step 1
      if (!formData.name || !formData.email || !formData.phone) {
        alert("Veuillez remplir tous les champs");
        return;
      }
      setStep(2);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        annual_income: parseInt(formData.annual_income),
        annual_tax_amount: parseInt(formData.annual_tax_amount)
      };

      const response = await axios.post(`${API}/leads`, payload);
      const lead = response.data;

      // Track Google Ads conversion
      if (window.gtag && lead.is_qualified) {
        window.gtag('event', 'conversion', {
          'send_to': 'AW-18068773571/qualified_lead',
          'value': 1.0,
          'currency': 'EUR'
        });
      }

      // Navigate based on qualification
      if (lead.is_qualified) {
        navigate('/qualified', { state: { lead } });
      } else {
        navigate('/not-qualified');
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Header */}
      <header className="container mx-auto px-4 py-6">
        <div className="flex items-center space-x-2">
          <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-xl">€</span>
          </div>
          <span className="text-white text-xl font-semibold">FiscalOptim</span>
        </div>
      </header>

      {/* Hero Section */}
      <div className="container mx-auto px-4 py-12 md:py-20">
        <div className="max-w-4xl mx-auto text-center mb-12">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Réduisez Vos Impôts en Belgique
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-600 mt-2">
              Jusqu'à 30% d'Économies
            </span>
          </h1>
          <p className="text-xl text-blue-200 mb-4">
            Solutions fiscales personnalisées pour <strong>salariés, entrepreneurs, indépendants et investisseurs</strong>.
          </p>
          <p className="text-lg text-blue-100 mb-8">
            Optimisez votre situation fiscale et préparez votre pension sereinement.
          </p>
          
          {/* Urgence Banner */}
          <div className="bg-gradient-to-r from-red-500 to-orange-500 text-white py-4 px-6 rounded-lg mb-8 animate-pulse">
            <p className="text-lg font-semibold">
              ⚠️ N'attendez pas la fin de l'année ! Agissez maintenant pour maximiser vos économies fiscales 2026
            </p>
          </div>
        </div>

        {/* Section Pourquoi Agir Maintenant */}
        <div className="max-w-5xl mx-auto mb-16">
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 md:p-12 border border-white/20">
            <h2 className="text-3xl font-bold text-white text-center mb-8">
              Pourquoi Agir <span className="text-yellow-400">Maintenant</span> ?
            </h2>
            
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white/5 p-6 rounded-xl border border-white/10">
                <div className="text-4xl mb-3">⏰</div>
                <h3 className="text-xl font-semibold text-white mb-2">Temps = Argent</h3>
                <p className="text-blue-200">
                  Plus vous agissez tôt dans l'année, plus vous pouvez optimiser vos déductions fiscales et mettre en place des stratégies efficaces.
                </p>
              </div>
              
              <div className="bg-white/5 p-6 rounded-xl border border-white/10">
                <div className="text-4xl mb-3">💰</div>
                <h3 className="text-xl font-semibold text-white mb-2">Évitez la Précipitation</h3>
                <p className="text-blue-200">
                  En décembre, il est souvent trop tard pour certaines optimisations. Agir maintenant vous donne toutes les options.
                </p>
              </div>
              
              <div className="bg-white/5 p-6 rounded-xl border border-white/10">
                <div className="text-4xl mb-3">📈</div>
                <h3 className="text-xl font-semibold text-white mb-2">Bénéfices Immédiats</h3>
                <p className="text-blue-200">
                  Commencez à économiser dès maintenant et préparez votre avenir fiscal sereinement, mois après mois.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Section Commencer Jeune */}
        <div className="max-w-5xl mx-auto mb-16">
          <div className="bg-gradient-to-br from-green-900/40 to-blue-900/40 backdrop-blur-sm rounded-2xl p-8 md:p-12 border border-green-400/30">
            <h2 className="text-3xl font-bold text-white text-center mb-6">
              🎯 L'Importance de Commencer <span className="text-green-400">Jeune</span>
            </h2>
            
            <p className="text-xl text-center text-blue-100 mb-8">
              Que vous soyez <strong>salarié</strong>, <strong>indépendant</strong> ou <strong>entrepreneur</strong>, 
              plus vous commencez tôt à optimiser votre fiscalité et votre pension, plus vous en profitez !
            </p>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <h3 className="text-2xl font-semibold text-yellow-400 mb-4">💡 Saviez-vous que ?</h3>
                <div className="space-y-3 text-blue-100">
                  <p className="flex items-start">
                    <span className="text-green-400 mr-2 text-xl">✓</span>
                    <span>Commencer à 30 ans vs 40 ans peut vous faire gagner <strong className="text-white">jusqu'à 150 000€</strong> à la pension</span>
                  </p>
                  <p className="flex items-start">
                    <span className="text-green-400 mr-2 text-xl">✓</span>
                    <span>Les <strong className="text-white">intérêts composés</strong> sont votre meilleur allié : chaque année compte double</span>
                  </p>
                  <p className="flex items-start">
                    <span className="text-green-400 mr-2 text-xl">✓</span>
                    <span>Les salariés ont accès à des <strong className="text-white">plans de pension</strong> fiscalement avantageux souvent méconnus</span>
                  </p>
                  <p className="flex items-start">
                    <span className="text-green-400 mr-2 text-xl">✓</span>
                    <span>Une stratégie fiscale bien pensée dès 25-30 ans = <strong className="text-white">sérénité</strong> à 60 ans</span>
                  </p>
                </div>
              </div>
              
              <div className="bg-white/10 p-6 rounded-xl">
                <h3 className="text-xl font-semibold text-white mb-4">📊 Exemple Concret</h3>
                <div className="space-y-4 text-blue-100">
                  <div className="border-l-4 border-yellow-400 pl-4">
                    <p className="font-semibold text-white">Marie, 28 ans, salariée</p>
                    <p className="text-sm">Commence aujourd'hui : 200€/mois en pension complémentaire</p>
                    <p className="text-green-400 font-bold mt-2">À 65 ans : +320 000€ 🎉</p>
                  </div>
                  <div className="border-l-4 border-orange-400 pl-4">
                    <p className="font-semibold text-white">Pierre, 45 ans, salarié</p>
                    <p className="text-sm">Commence maintenant : 200€/mois en pension complémentaire</p>
                    <p className="text-yellow-400 font-bold mt-2">À 65 ans : +85 000€</p>
                  </div>
                  <p className="text-center text-yellow-300 font-semibold mt-4">
                    ⚡ Différence : 235 000€ !
                  </p>
                </div>
              </div>
            </div>
            
            <div className="mt-8 bg-yellow-500/20 border border-yellow-400/50 rounded-lg p-6 text-center">
              <p className="text-xl font-semibold text-white mb-2">
                🚀 Ne laissez pas passer les années les plus profitables
              </p>
              <p className="text-blue-100">
                Même avec un salaire modeste, commencer jeune fait <strong>toute la différence</strong>
              </p>
            </div>
          </div>
        </div>

        {/* Témoignages */}
        <div className="max-w-5xl mx-auto mb-16">
          <h2 className="text-3xl font-bold text-white text-center mb-8">
            Ce Que Disent Nos Clients
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-lg">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-xl">
                  SL
                </div>
                <div className="ml-3">
                  <p className="font-semibold text-gray-900">Sophie L.</p>
                  <p className="text-sm text-gray-600">Salariée, 32 ans</p>
                </div>
              </div>
              <div className="text-yellow-400 mb-2">★★★★★</div>
              <p className="text-gray-700 text-sm">
                "J'ai économisé <strong>4 200€ d'impôts</strong> dès la première année ! Je regrette de ne pas avoir commencé plus tôt."
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-lg">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center text-white font-bold text-xl">
                  MD
                </div>
                <div className="ml-3">
                  <p className="font-semibold text-gray-900">Marc D.</p>
                  <p className="text-sm text-gray-600">Indépendant, 41 ans</p>
                </div>
              </div>
              <div className="text-yellow-400 mb-2">★★★★★</div>
              <p className="text-gray-700 text-sm">
                "Une approche claire et personnalisée. Ma pension est maintenant optimisée et je paie <strong>30% d'impôts en moins</strong>."
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-lg">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold text-xl">
                  EB
                </div>
                <div className="ml-3">
                  <p className="font-semibold text-gray-900">Emma B.</p>
                  <p className="text-sm text-gray-600">Salariée, 27 ans</p>
                </div>
              </div>
              <div className="text-yellow-400 mb-2">★★★★★</div>
              <p className="text-gray-700 text-sm">
                "J'ai 27 ans et j'ai déjà commencé à préparer ma pension. Mon conseiller m'a montré que <strong>commencer jeune change tout</strong>."
              </p>
            </div>
          </div>
        </div>

        {/* Form Card */}
        <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-2xl p-8 md:p-12">
          {/* Progress Indicator */}
          <div className="flex items-center justify-center mb-8">
            <div className="flex items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${step === 1 ? 'bg-blue-600 text-white' : 'bg-green-500 text-white'}`}>
                {step === 1 ? '1' : '✓'}
              </div>
              <div className={`w-16 h-1 mx-2 ${step === 2 ? 'bg-blue-600' : 'bg-gray-300'}`}></div>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${step === 2 ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'}`}>
                2
              </div>
            </div>
          </div>

          <form onSubmit={step === 1 ? handleNextStep : handleSubmit}>
            {/* STEP 1: Basic Information */}
            {step === 1 && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 text-center mb-6">
                  Vos Coordonnées
                </h2>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom complet *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Jean Dupont"
                    required
                    data-testid="input-name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="jean.dupont@example.com"
                    required
                    data-testid="input-email"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Téléphone *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="+32 xxx xx xx xx"
                    required
                    data-testid="input-phone"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 rounded-lg transition-colors duration-200"
                  data-testid="btn-next-step"
                >
                  Continuer
                </button>
              </div>
            )}

            {/* STEP 2: Qualification Questions */}
            {step === 2 && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 text-center mb-6">
                  Votre Situation Fiscale
                </h2>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Revenu annuel brut (€) *
                  </label>
                  <select
                    name="annual_income"
                    value={formData.annual_income}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                    data-testid="select-income"
                  >
                    <option value="">Sélectionnez...</option>
                    <option value="25000">Moins de 25 000 €</option>
                    <option value="40000">25 000 - 40 000 €</option>
                    <option value="55000">40 000 - 60 000 €</option>
                    <option value="75000">60 000 - 90 000 €</option>
                    <option value="120000">90 000 - 150 000 €</option>
                    <option value="200000">Plus de 150 000 €</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Votre profil *
                  </label>
                  <select
                    name="profile_type"
                    value={formData.profile_type}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                    data-testid="select-profile"
                  >
                    <option value="">Sélectionnez...</option>
                    <option value="independant">Indépendant</option>
                    <option value="chef_entreprise">Chef d'entreprise</option>
                    <option value="salarie">Salarié</option>
                    <option value="investisseur">Investisseur</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Montant d'impôts payés annuellement (€) *
                  </label>
                  <select
                    name="annual_tax_amount"
                    value={formData.annual_tax_amount}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                    data-testid="select-tax"
                  >
                    <option value="">Sélectionnez...</option>
                    <option value="3000">Moins de 5 000 €</option>
                    <option value="7000">5 000 - 10 000 €</option>
                    <option value="12000">10 000 - 15 000 €</option>
                    <option value="20000">15 000 - 25 000 €</option>
                    <option value="35000">25 000 - 50 000 €</option>
                    <option value="60000">Plus de 50 000 €</option>
                  </select>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg space-y-4">
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      name="needs_urgent_action"
                      checked={formData.needs_urgent_action}
                      onChange={handleChange}
                      className="mt-1 mr-3 h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      data-testid="checkbox-urgent"
                    />
                    <label className="text-sm text-gray-700">
                      Je souhaite <strong>agir rapidement</strong> pour diminuer mes impôts dès cette année
                    </label>
                  </div>

                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      name="preparing_pension"
                      checked={formData.preparing_pension}
                      onChange={handleChange}
                      className="mt-1 mr-3 h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      data-testid="checkbox-pension"
                    />
                    <label className="text-sm text-gray-700">
                      Je souhaite <strong>préparer ma pension</strong> de manière fiscalement avantageuse
                    </label>
                  </div>
                </div>

                <div className="flex space-x-4">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-4 rounded-lg transition-colors duration-200"
                    data-testid="btn-back"
                  >
                    Retour
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-white font-semibold py-4 rounded-lg transition-colors duration-200 disabled:opacity-50"
                    data-testid="btn-submit"
                  >
                    {loading ? "Analyse en cours..." : "Voir mon éligibilité"}
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>

        {/* Trust Indicators */}
        <div className="max-w-4xl mx-auto mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
          <div className="text-white">
            <div className="text-4xl font-bold text-yellow-400 mb-2">500+</div>
            <div className="text-blue-200">Clients satisfaits</div>
          </div>
          <div className="text-white">
            <div className="text-4xl font-bold text-yellow-400 mb-2">€2.5M+</div>
            <div className="text-blue-200">Économies générées</div>
          </div>
          <div className="text-white">
            <div className="text-4xl font-bold text-yellow-400 mb-2">15 ans</div>
            <div className="text-blue-200">D'expertise fiscale</div>
          </div>
        </div>

        {/* FSMA Compliance Notice */}
        <div className="max-w-4xl mx-auto mt-12 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-6 text-center">
          <p className="text-sm text-blue-100">
            ℹ️ Les consultations personnalisées sont réalisées sous supervision FSMA. 
            Les informations fournies sur ce site sont à titre éducatif uniquement.
          </p>
        </div>

        {/* Footer with Legal Links */}
        <div className="max-w-4xl mx-auto mt-8 pt-6 border-t border-white/20">
          <div className="flex flex-wrap justify-center gap-4 text-sm text-blue-100">
            <Link to="/politique-confidentialite" className="hover:text-white transition-colors">
              Politique de Confidentialité
            </Link>
            <span className="text-white/40">|</span>
            <Link to="/mentions-legales" className="hover:text-white transition-colors">
              Mentions Légales
            </Link>
            <span className="text-white/40">|</span>
            <Link to="/conditions-utilisation" className="hover:text-white transition-colors">
              Conditions d'Utilisation
            </Link>
          </div>
          <p className="text-center text-sm text-blue-100 mt-4">
            © {new Date().getFullYear()} DMCA Consulting - Tous droits réservés
          </p>
        </div>
      </div>
    </div>
  );
};

// ============== QUALIFIED PAGE ==============
const QualifiedPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-white rounded-2xl shadow-2xl p-8 md:p-12 text-center">
        <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path>
          </svg>
        </div>

        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
          Félicitations ! Vous êtes éligible 🎉
        </h1>
        
        <p className="text-lg text-gray-700 mb-8">
          Votre profil correspond parfaitement à nos solutions fiscales premium. 
          Réservez dès maintenant votre <strong>consultation gratuite</strong> avec l'un de nos experts fiscaux.
        </p>

        <div className="bg-blue-50 p-6 rounded-lg mb-8">
          <h3 className="font-semibold text-gray-900 mb-3">Ce que vous obtiendrez :</h3>
          <ul className="text-left space-y-2 text-gray-700">
            <li className="flex items-start">
              <span className="text-green-500 mr-2">✓</span>
              Analyse personnalisée de votre situation fiscale
            </li>
            <li className="flex items-start">
              <span className="text-green-500 mr-2">✓</span>
              Plan d'action concret pour réduire vos impôts
            </li>
            <li className="flex items-start">
              <span className="text-green-500 mr-2">✓</span>
              Stratégie de pension optimisée fiscalement
            </li>
            <li className="flex items-start">
              <span className="text-green-500 mr-2">✓</span>
              Accompagnement personnalisé par un expert
            </li>
          </ul>
        </div>

        <button
          onClick={() => navigate('/book-appointment')}
          className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold py-4 px-8 rounded-lg text-lg transition-colors duration-200"
          data-testid="btn-book-appointment"
        >
          Réserver mon entretien Zoom gratuit
        </button>

        <p className="text-sm text-gray-500 mt-4">
          Durée : 30 minutes • Sans engagement
        </p>
      </div>
    </div>
  );
};

// ============== NOT QUALIFIED PAGE ==============
const NotQualifiedPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-yellow-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-white rounded-2xl shadow-2xl p-8 md:p-12 text-center">
        <div className="w-20 h-20 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-6">
          <span className="text-4xl">💼</span>
        </div>

        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
          Merci pour votre intérêt
        </h1>
        
        <p className="text-lg text-gray-700 mb-8">
          Votre profil ne correspond pas encore à nos solutions premium, mais nous pouvons tout de même vous aider !
        </p>

        <div className="bg-blue-50 p-6 rounded-lg mb-8">
          <h3 className="font-semibold text-gray-900 mb-3">Restez en contact :</h3>
          <p className="text-gray-700 mb-4">
            Nous proposons également des solutions adaptées à différents profils. 
            Contactez-nous pour discuter de vos besoins spécifiques.
          </p>
          
          <div className="space-y-2 text-left">
            <p className="text-gray-700">
              <strong>Email :</strong> contact@fiscaloptim.be
            </p>
            <p className="text-gray-700">
              <strong>Téléphone :</strong> +32 2 XXX XX XX
            </p>
          </div>
        </div>

        <a
          href="/"
          className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-lg transition-colors duration-200"
          data-testid="btn-back-home"
        >
          Retour à l'accueil
        </a>
      </div>
    </div>
  );
};

// ============== BOOK APPOINTMENT PAGE ==============
const BookAppointment = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [slots, setSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTime, setSelectedTime] = useState("");
  const [formData, setFormData] = useState({
    lead_name: "",
    lead_email: "",
    lead_phone: ""
  });

  useEffect(() => {
    fetchSlots();
  }, []);

  const fetchSlots = async () => {
    try {
      const response = await axios.get(`${API}/available-slots`);
      setSlots(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching slots:", error);
      setLoading(false);
    }
  };

  const slotsByDate = slots.reduce((acc, slot) => {
    if (!acc[slot.date]) {
      acc[slot.date] = [];
    }
    acc[slot.date].push(slot);
    return acc;
  }, {});

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedTime("");
  };

  const handleTimeSelect = (time) => {
    setSelectedTime(time);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedDate || !selectedTime) {
      alert("Veuillez sélectionner une date et une heure");
      return;
    }

    if (!formData.lead_name || !formData.lead_email || !formData.lead_phone) {
      alert("Veuillez remplir toutes vos coordonnées");
      return;
    }

    try {
      const payload = {
        lead_id: `lead_${Date.now()}`,
        lead_name: formData.lead_name,
        lead_email: formData.lead_email,
        lead_phone: formData.lead_phone,
        appointment_date: selectedDate,
        appointment_time: selectedTime
      };

      await axios.post(`${API}/appointments`, payload);
      
      navigate('/confirmation', { 
        state: { 
          date: selectedDate, 
          time: selectedTime,
          name: formData.lead_name 
        } 
      });
    } catch (error) {
      console.error("Error booking appointment:", error);
      if (error.response?.status === 400) {
        alert("Ce créneau vient d'être réservé. Veuillez en choisir un autre.");
        fetchSlots();
      } else {
        alert("Une erreur est survenue. Veuillez réessayer.");
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des créneaux disponibles...</p>
        </div>
      </div>
    );
  }

  const availableDates = Object.keys(slotsByDate).slice(0, 10);

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2 text-center">
            Réservez votre entretien
          </h1>
          <p className="text-gray-600 text-center mb-8">
            Choisissez un créneau qui vous convient
          </p>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Coordonnées */}
            <div className="border-b pb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Vos coordonnées</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <input
                  type="text"
                  name="lead_name"
                  value={formData.lead_name}
                  onChange={handleChange}
                  placeholder="Nom complet"
                  className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
                <input
                  type="email"
                  name="lead_email"
                  value={formData.lead_email}
                  onChange={handleChange}
                  placeholder="Email"
                  className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
                <input
                  type="tel"
                  name="lead_phone"
                  value={formData.lead_phone}
                  onChange={handleChange}
                  placeholder="Téléphone"
                  className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
            </div>

            {/* Date Selection */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Choisissez une date</h2>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {availableDates.map(date => {
                  const dateObj = new Date(date + 'T00:00:00');
                  const dayName = dateObj.toLocaleDateString('fr-BE', { weekday: 'short' });
                  const dayNum = dateObj.getDate();
                  const monthName = dateObj.toLocaleDateString('fr-BE', { month: 'short' });
                  
                  return (
                    <button
                      key={date}
                      type="button"
                      onClick={() => handleDateSelect(date)}
                      className={`p-4 border-2 rounded-lg text-center transition-colors ${
                        selectedDate === date
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-300 hover:border-blue-400'
                      }`}
                    >
                      <div className="text-sm text-gray-600 capitalize">{dayName}</div>
                      <div className="text-2xl font-bold text-gray-900">{dayNum}</div>
                      <div className="text-sm text-gray-600 capitalize">{monthName}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Time Selection */}
            {selectedDate && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">2. Choisissez une heure</h2>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                  {slotsByDate[selectedDate]
                    ?.filter(slot => slot.available)
                    .map(slot => (
                      <button
                        key={slot.time}
                        type="button"
                        onClick={() => handleTimeSelect(slot.time)}
                        className={`py-3 px-4 border-2 rounded-lg font-medium transition-colors ${
                          selectedTime === slot.time
                            ? 'border-blue-600 bg-blue-600 text-white'
                            : 'border-gray-300 hover:border-blue-400 text-gray-700'
                        }`}
                      >
                        {slot.time}
                      </button>
                    ))}
                </div>
                {slotsByDate[selectedDate]?.filter(slot => slot.available).length === 0 && (
                  <p className="text-gray-500 text-center py-4">Aucun créneau disponible pour cette date</p>
                )}
              </div>
            )}

            {/* Submit Button */}
            {selectedDate && selectedTime && (
              <div className="pt-6">
                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold py-4 rounded-lg text-lg transition-colors duration-200"
                >
                  Confirmer mon rendez-vous
                </button>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

// ============== CONFIRMATION PAGE ==============
const ConfirmationPage = () => {
  const location = useLocation();
  const state = location.state || {};
  const { date = "", time = "", name = "Client" } = state;

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const dateObj = new Date(dateStr + 'T00:00:00');
    return dateObj.toLocaleDateString('fr-BE', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-white rounded-2xl shadow-2xl p-8 md:p-12 text-center">
        <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path>
          </svg>
        </div>

        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
          Rendez-vous confirmé ! 🎉
        </h1>
        
        <p className="text-lg text-gray-700 mb-8">
          Merci {name} ! Votre entretien avec notre expert fiscal est confirmé.
        </p>

        <div className="bg-blue-50 p-6 rounded-lg mb-8 text-left">
          <h3 className="font-semibold text-gray-900 mb-4 text-center">Détails de votre rendez-vous :</h3>
          <div className="space-y-3">
            <div className="flex items-center">
              <svg className="w-6 h-6 text-blue-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
              </svg>
              <div>
                <div className="text-sm text-gray-600">Date</div>
                <div className="font-semibold text-gray-900 capitalize">{formatDate(date)}</div>
              </div>
            </div>
            <div className="flex items-center">
              <svg className="w-6 h-6 text-blue-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <div>
                <div className="text-sm text-gray-600">Heure</div>
                <div className="font-semibold text-gray-900">{time}</div>
              </div>
            </div>
            <div className="flex items-center">
              <svg className="w-6 h-6 text-blue-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
              </svg>
              <div>
                <div className="text-sm text-gray-600">Plateforme</div>
                <div className="font-semibold text-gray-900">Zoom (lien envoyé par email)</div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg mb-6">
          <p className="text-sm text-gray-700">
            <strong>Important :</strong> Vous recevrez un email de confirmation avec le lien Zoom 
            et les instructions pour rejoindre l'entretien.
          </p>
        </div>

        <div className="space-y-4">
          <a
            href="/"
            className="block w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-lg transition-colors duration-200"
            data-testid="btn-back-home-confirmed"
          >
            Retour à l'accueil
          </a>
          
          <p className="text-sm text-gray-500">
            Questions ? Contactez-nous à contact@fiscaloptim.be
          </p>
        </div>
      </div>
    </div>
  );
};

// ============== HEALTHCARE PROFESSIONALS PAGE ==============
const HealthcareProfessionals = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    profile_type: ""
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.profile_type) {
      alert("Veuillez remplir tous les champs");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        profile_type: formData.profile_type,
        source: "healthcare",
        annual_income: 0,
        annual_tax_amount: 0,
        needs_urgent_action: true,
        preparing_pension: true
      };

      const response = await axios.post(`${API}/leads`, payload);
      const lead = response.data;

      // Track Google Ads conversion for healthcare professionals
      if (window.gtag && lead.is_qualified) {
        window.gtag('event', 'conversion', {
          'send_to': 'AW-18068773571/healthcare_lead',
          'value': 1.0,
          'currency': 'EUR'
        });
      }

      // All healthcare professionals are qualified
      if (lead.is_qualified) {
        navigate('/qualified-healthcare');
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-900 via-blue-900 to-teal-900">
      {/* Header */}
      <header className="container mx-auto px-4 py-6">
        <div className="flex items-center space-x-2">
          <div className="w-10 h-10 bg-gradient-to-br from-teal-400 to-teal-600 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-xl">+</span>
          </div>
          <span className="text-white text-xl font-semibold">PatrimoineSanté</span>
        </div>
      </header>

      {/* Hero Section with Image */}
      <div className="container mx-auto px-4 py-12 md:py-20">
        <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
          {/* Left: Text Content */}
          <div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Professionnels de Santé
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-green-400 mt-2">
                Préparez Votre Pension & Investissez
              </span>
            </h1>
            <p className="text-xl text-blue-200 mb-6">
              Plan social sur mesure pour <strong>médecins, dentistes, kinés et infirmiers indépendants</strong>.
            </p>
            <p className="text-lg text-blue-100 mb-8">
              Préparez votre pension tout en réalisant votre projet immobilier : 
              cabinet médical, immeuble de rapport, ou résidence.
            </p>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <h3 className="text-xl font-semibold text-white mb-4">✨ Votre Plan Social Inclut :</h3>
              <ul className="space-y-3 text-blue-100">
                <li className="flex items-start">
                  <span className="text-teal-400 mr-2 text-xl">✓</span>
                  <span>Constitution optimisée de votre pension complémentaire</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-400 mr-2 text-xl">✓</span>
                  <span>Financement de votre projet immobilier professionnel</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-400 mr-2 text-xl">✓</span>
                  <span>Avantages fiscaux maximisés pour indépendants</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-400 mr-2 text-xl">✓</span>
                  <span>Patrimoine immobilier + sécurité de la pension</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Right: Hero Image */}
          <div className="relative">
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxkb2N0b3J8ZW58MHx8fHwxNzczNzU4OTE2fDA&ixlib=rb-4.1.0&q=85"
                alt="Professionnel de santé"
                className="w-full h-auto"
              />
            </div>
            <div className="absolute -bottom-6 -right-6 bg-teal-500 text-white p-6 rounded-xl shadow-lg">
              <div className="text-3xl font-bold">2 en 1</div>
              <div className="text-sm">Pension + Immobilier</div>
            </div>
          </div>
        </div>

        {/* Why This Plan Section */}
        <div className="max-w-5xl mx-auto mb-16">
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 md:p-12 border border-white/20">
            <h2 className="text-3xl font-bold text-white text-center mb-8">
              Pourquoi Ce Plan Est Fait Pour Vous
            </h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white/5 p-6 rounded-xl border border-white/10">
                <div className="text-4xl mb-3">🏥</div>
                <h3 className="text-xl font-semibold text-white mb-2">Spécial Santé</h3>
                <p className="text-blue-200">
                  Solution adaptée aux revenus et besoins spécifiques des professionnels de santé indépendants.
                </p>
              </div>
              
              <div className="bg-white/5 p-6 rounded-xl border border-white/10">
                <div className="text-4xl mb-3">🏢</div>
                <h3 className="text-xl font-semibold text-white mb-2">Projet Immobilier</h3>
                <p className="text-blue-200">
                  Financez l'achat de votre cabinet, un immeuble de rapport ou votre résidence via votre plan social.
                </p>
              </div>
              
              <div className="bg-white/5 p-6 rounded-xl border border-white/10">
                <div className="text-4xl mb-3">💰</div>
                <h3 className="text-xl font-semibold text-white mb-2">Double Avantage</h3>
                <p className="text-blue-200">
                  Constituez votre pension tout en créant un patrimoine immobilier. Le meilleur des deux mondes.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Real Example Section */}
        <div className="max-w-5xl mx-auto mb-16">
          <div className="bg-gradient-to-br from-green-900/40 to-teal-900/40 backdrop-blur-sm rounded-2xl p-8 md:p-12 border border-teal-400/30">
            <h2 className="text-3xl font-bold text-white text-center mb-6">
              📊 Exemple Concret
            </h2>
            
            <div className="bg-white/10 p-8 rounded-xl mb-6">
              <h3 className="text-2xl font-semibold text-white mb-4">
                Dr. Sophie M., Dentiste indépendante, 38 ans
              </h3>
              
              <div className="space-y-4 text-blue-100">
                <div className="flex items-start">
                  <span className="text-teal-400 mr-3 text-2xl">→</span>
                  <div>
                    <p className="font-semibold text-white">Objectif :</p>
                    <p>Acheter son propre cabinet dentaire (300 000€) et préparer sa pension</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <span className="text-teal-400 mr-3 text-2xl">→</span>
                  <div>
                    <p className="font-semibold text-white">Solution :</p>
                    <p>Plan social avec financement immobilier intégré</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <span className="text-teal-400 mr-3 text-2xl">→</span>
                  <div>
                    <p className="font-semibold text-white">Résultat :</p>
                    <ul className="list-disc list-inside ml-4 space-y-1">
                      <li>Cabinet acheté et remboursé via le plan</li>
                      <li>Pension complémentaire de 450 000€ à 65 ans</li>
                      <li>Avantages fiscaux annuels de 8 000€</li>
                      <li>Patrimoine immobilier de 300 000€</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="text-center">
              <p className="text-xl font-semibold text-teal-300">
                💡 Votre cabinet devient votre pension !
              </p>
            </div>
          </div>
        </div>

        {/* Form Card */}
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">
              Recevez Votre Plan Personnalisé
            </h2>
            <p className="text-center text-gray-600 mb-8">
              Consultation gratuite avec un expert en patrimoine médical
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom complet *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="Dr. Jean Dupont"
                  required
                  data-testid="input-name-healthcare"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email professionnel *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="dr.dupont@cabinet.be"
                  required
                  data-testid="input-email-healthcare"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Téléphone *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="+32 xxx xx xx xx"
                  required
                  data-testid="input-phone-healthcare"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Votre profession *
                </label>
                <select
                  name="profile_type"
                  value={formData.profile_type}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  required
                  data-testid="select-profession-healthcare"
                >
                  <option value="">Sélectionnez...</option>
                  <option value="medecin">Médecin indépendant</option>
                  <option value="dentiste">Dentiste</option>
                  <option value="kine">Kinésithérapeute</option>
                  <option value="infirmier">Infirmier(ère) indépendant(e)</option>
                  <option value="autre_sante">Autre professionnel de santé</option>
                </select>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-teal-500 to-green-500 hover:from-teal-600 hover:to-green-600 text-white font-semibold py-4 rounded-lg transition-colors duration-200 disabled:opacity-50"
                data-testid="btn-submit-healthcare"
              >
                {loading ? "Envoi en cours..." : "Recevoir mon plan personnalisé"}
              </button>

              <p className="text-center text-sm text-gray-500">
                ✓ Sans engagement • ✓ Consultation gratuite • ✓ Réponse sous 24h
              </p>

              <div className="mt-4 text-center text-xs text-gray-500 bg-gray-50 p-3 rounded">
                ℹ️ Les consultations personnalisées sont réalisées sous supervision FSMA
              </div>
            </form>
          </div>
        </div>

        {/* Trust Section */}
        <div className="max-w-4xl mx-auto mt-16 text-center">
          <p className="text-blue-200 text-lg mb-6">
            Déjà <strong className="text-white">200+ professionnels de santé</strong> nous font confiance
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="text-3xl mb-2">👨‍⚕️</div>
              <div className="text-sm text-blue-200">Médecins</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="text-3xl mb-2">🦷</div>
              <div className="text-sm text-blue-200">Dentistes</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="text-3xl mb-2">💪</div>
              <div className="text-sm text-blue-200">Kinés</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="text-3xl mb-2">👩‍⚕️</div>
              <div className="text-sm text-blue-200">Infirmiers</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============== QUALIFIED HEALTHCARE PAGE ==============
const QualifiedHealthcarePage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-teal-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-white rounded-2xl shadow-2xl p-8 md:p-12 text-center">
        <div className="w-20 h-20 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path>
          </svg>
        </div>

        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
          Parfait ! Votre demande est enregistrée 🎉
        </h1>
        
        <p className="text-lg text-gray-700 mb-8">
          Notre expert en patrimoine médical vous contactera dans les <strong>24 heures</strong> pour 
          discuter de votre plan social personnalisé et de votre projet immobilier.
        </p>

        <div className="bg-teal-50 p-6 rounded-lg mb-8">
          <h3 className="font-semibold text-gray-900 mb-3">Lors de votre consultation :</h3>
          <ul className="text-left space-y-2 text-gray-700">
            <li className="flex items-start">
              <span className="text-teal-500 mr-2">✓</span>
              <span>Analyse de votre situation professionnelle et patrimoniale</span>
            </li>
            <li className="flex items-start">
              <span className="text-teal-500 mr-2">✓</span>
              <span>Plan social sur mesure pour votre pension</span>
            </li>
            <li className="flex items-start">
              <span className="text-teal-500 mr-2">✓</span>
              <span>Options de financement pour votre projet immobilier</span>
            </li>
            <li className="flex items-start">
              <span className="text-teal-500 mr-2">✓</span>
              <span>Stratégie fiscale optimisée pour professionnels de santé</span>
            </li>
          </ul>
        </div>

        <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mb-6 text-sm text-gray-700">
          <p>
            ℹ️ <strong>Note de conformité :</strong> Les consultations personnalisées sont réalisées 
            sous supervision FSMA. Les informations sur ce site sont à titre éducatif uniquement 
            et ne constituent pas un conseil en investissement personnalisé.
          </p>
        </div>

        <a
          href="/professionnels-sante"
          className="inline-block bg-teal-600 hover:bg-teal-700 text-white font-semibold py-3 px-8 rounded-lg transition-colors duration-200"
        >
          Retour à l'accueil
        </a>

        <p className="text-sm text-gray-500 mt-6">
          Questions ? Contactez-nous à contact@patrimoine-sante.be
        </p>
      </div>
    </div>
  );
};

// ============== BLOG LIST PAGE ==============
const BlogList = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-6">
          <Link to="/" className="flex items-center space-x-2 w-fit">
            <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl">€</span>
            </div>
            <span className="text-gray-900 text-xl font-semibold">FiscalOptim Blog</span>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Conseils Fiscaux & Pension
          </h1>
          <p className="text-xl text-blue-100">
            Guides pratiques pour optimiser votre fiscalité en Belgique
          </p>
        </div>
      </div>

      {/* Articles Grid */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid md:grid-cols-3 gap-8">
          {articles.map(article => (
            <Link
              key={article.id}
              to={`/blog/${article.slug}`}
              className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-shadow overflow-hidden"
            >
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-48 object-cover"
              />
              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">
                    {article.category}
                  </span>
                  <span className="text-sm text-gray-500">{article.readTime}</span>
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2">
                  {article.title}
                </h2>
                <p className="text-gray-600 text-sm line-clamp-3 mb-4">
                  {article.excerpt}
                </p>
                <div className="flex items-center text-sm text-gray-500">
                  <span>{new Date(article.date).toLocaleDateString('fr-BE', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

// ============== BLOG ARTICLE PAGE ==============
const BlogArticle = () => {
  const { slug } = useParams();
  const article = getArticleBySlug(slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Article non trouvé</h1>
          <Link to="/blog" className="text-blue-600 hover:underline">
            Retour au blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-6">
          <Link to="/blog" className="flex items-center space-x-2 w-fit hover:opacity-80">
            <span className="text-blue-600">← Retour au blog</span>
          </Link>
        </div>
      </header>

      {/* Article Hero */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="flex items-center space-x-4 mb-6">
            <span className="px-3 py-1 bg-white/20 text-white text-sm font-semibold rounded-full">
              {article.category}
            </span>
            <span className="text-blue-100">{article.readTime}</span>
            <span className="text-blue-100">
              {new Date(article.date).toLocaleDateString('fr-BE', { day: 'numeric', month: 'long', year: 'numeric' })}
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            {article.title}
          </h1>
          <p className="text-xl text-blue-100">
            {article.excerpt}
          </p>
        </div>
      </div>

      {/* Article Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-xl shadow-sm p-8 md:p-12">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-96 object-cover rounded-lg mb-8"
            />
            <div
              className="prose prose-lg max-w-none"
              dangerouslySetInnerHTML={{ __html: article.content.replace(/\n/g, '<br/>') }}
            />
          </div>

          {/* CTA */}
          <div className="mt-12 bg-gradient-to-r from-blue-600 to-blue-800 rounded-xl p-8 text-center text-white">
            <h3 className="text-2xl font-bold mb-4">
              Prêt à Optimiser Votre Fiscalité ?
            </h3>
            <p className="text-blue-100 mb-2">
              Obtenez une analyse gratuite de votre situation fiscale
            </p>
            <p className="text-xs text-blue-200 mb-6">
              Consultations personnalisées réalisées sous supervision FSMA
            </p>
            <Link
              to="/"
              className="inline-block bg-white text-blue-600 font-semibold px-8 py-3 rounded-lg hover:bg-blue-50 transition-colors"
            >
              Demander mon analyse gratuite
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============== ADMIN DASHBOARD ==============
const AdminDashboard = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("prospects");
  const [filterPriority, setFilterPriority] = useState("all");
  const [filterSource, setFilterSource] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [leadsRes, appointmentsRes] = await Promise.all([
        axios.get(`${API}/leads`, { withCredentials: true }),
        axios.get(`${API}/appointments`, { withCredentials: true })
      ]);
      setLeads(leadsRes.data);
      setAppointments(appointmentsRes.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 401) {
        navigate('/login');
      }
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  // Statistics
  const stats = {
    totalLeads: leads.length,
    qualified: leads.filter(l => l.is_qualified).length,
    highPriority: leads.filter(l => l.priority_level === "high").length,
    standard: leads.filter(l => l.priority_level === "standard").length,
    appointments: appointments.length
  };

  // Filter leads
  const filteredLeads = leads
    .filter(lead => {
      if (filterPriority === "all") return true;
      if (filterPriority === "qualified") return lead.is_qualified;
      if (filterPriority === "high") return lead.priority_level === "high";
      if (filterPriority === "standard") return lead.priority_level === "standard";
      if (filterPriority === "not-qualified") return !lead.is_qualified;
      return true;
    })
    .filter(lead => {
      if (filterSource === "all") return true;
      return lead.source === filterSource;
    })
    .filter(lead => {
      if (!searchTerm) return true;
      const search = searchTerm.toLowerCase();
      return lead.name.toLowerCase().includes(search) || 
             lead.email.toLowerCase().includes(search);
    });

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-BE', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPriorityBadge = (lead) => {
    if (!lead.is_qualified) {
      return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-200 text-gray-700">Non qualifié</span>;
    }
    if (lead.priority_level === "high") {
      return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">🔥 Haute Priorité</span>;
    }
    return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">📋 Standard</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement du dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-8 px-4">
        <div className="container mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold mb-2">Dashboard Admin</h1>
              <p className="text-blue-100">Gestion des prospects et rendez-vous</p>
            </div>
            <div className="flex items-center gap-4">
              <a
                href="/"
                className="bg-white/20 hover:bg-white/30 px-6 py-2 rounded-lg transition-colors"
              >
                ← Retour au site
              </a>
              <button
                onClick={handleLogout}
                className="bg-red-500/80 hover:bg-red-600 px-6 py-2 rounded-lg transition-colors font-semibold"
              >
                Déconnexion
              </button>
            </div>
          </div>

          {/* Statistics */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="text-3xl font-bold">{stats.totalLeads}</div>
              <div className="text-sm text-blue-100">Total Prospects</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="text-3xl font-bold">{stats.qualified}</div>
              <div className="text-sm text-blue-100">Qualifiés</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="text-3xl font-bold text-red-300">{stats.highPriority}</div>
              <div className="text-sm text-blue-100">🔥 Haute Priorité</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="text-3xl font-bold text-blue-300">{stats.standard}</div>
              <div className="text-sm text-blue-100">📋 Standard</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="text-3xl font-bold text-green-300">{stats.appointments}</div>
              <div className="text-sm text-blue-100">📅 Rendez-vous</div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="flex border-b">
            <button
              onClick={() => setActiveTab("prospects")}
              className={`px-6 py-4 font-semibold ${
                activeTab === "prospects"
                  ? "border-b-2 border-blue-600 text-blue-600"
                  : "text-gray-600 hover:text-gray-900"
              }`}
              data-testid="tab-prospects"
            >
              📋 Prospects ({stats.totalLeads})
            </button>
            <button
              onClick={() => setActiveTab("appointments")}
              className={`px-6 py-4 font-semibold ${
                activeTab === "appointments"
                  ? "border-b-2 border-blue-600 text-blue-600"
                  : "text-gray-600 hover:text-gray-900"
              }`}
              data-testid="tab-appointments"
            >
              📅 Rendez-vous ({stats.appointments})
            </button>
          </div>
        </div>

        {/* Prospects Tab */}
        {activeTab === "prospects" && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            {/* Filters */}
            <div className="mb-6 flex flex-col md:flex-row gap-4">
              <input
                type="text"
                placeholder="Rechercher par nom ou email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="search-input"
              />
              <select
                value={filterSource}
                onChange={(e) => setFilterSource(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="filter-source"
              >
                <option value="all">Toutes les sources</option>
                <option value="fiscal">Page Fiscale</option>
                <option value="healthcare">Professionnels Santé</option>
              </select>
              <select
                value={filterPriority}
                onChange={(e) => setFilterPriority(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="filter-priority"
              >
                <option value="all">Tous les prospects</option>
                <option value="qualified">Qualifiés uniquement</option>
                <option value="high">🔥 Haute Priorité</option>
                <option value="standard">📋 Standard</option>
                <option value="not-qualified">Non qualifiés</option>
              </select>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nom</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Téléphone</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Profil</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priorité</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredLeads.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                        Aucun prospect trouvé
                      </td>
                    </tr>
                  ) : (
                    filteredLeads.map((lead) => (
                      <tr key={lead.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4 text-sm font-medium text-gray-900">{lead.name}</td>
                        <td className="px-4 py-4 text-sm text-gray-600">{lead.email}</td>
                        <td className="px-4 py-4 text-sm text-gray-600">{lead.phone}</td>
                        <td className="px-4 py-4 text-sm">
                          {lead.source === "healthcare" ? (
                            <span className="px-2 py-1 rounded-full text-xs font-semibold bg-teal-100 text-teal-700">
                              🏥 Santé
                            </span>
                          ) : (
                            <span className="px-2 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-700">
                              💼 Fiscal
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-600 capitalize">
                          {lead.profile_type.replace('_', ' ')}
                        </td>
                        <td className="px-4 py-4 text-sm">{getPriorityBadge(lead)}</td>
                        <td className="px-4 py-4 text-sm text-gray-600">{formatDate(lead.created_at)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-4 text-sm text-gray-600">
              Affichage de {filteredLeads.length} sur {leads.length} prospects
            </div>
          </div>
        )}

        {/* Appointments Tab */}
        {activeTab === "appointments" && (
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nom</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Téléphone</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Heure</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Réservé le</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {appointments.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                        Aucun rendez-vous réservé
                      </td>
                    </tr>
                  ) : (
                    appointments
                      .sort((a, b) => new Date(a.appointment_date) - new Date(b.appointment_date))
                      .map((apt) => {
                        const aptDate = new Date(apt.appointment_date + 'T00:00:00');
                        const isPast = aptDate < new Date();
                        
                        return (
                          <tr key={apt.id} className={`hover:bg-gray-50 ${isPast ? 'opacity-60' : ''}`}>
                            <td className="px-4 py-4 text-sm font-medium text-gray-900">{apt.lead_name}</td>
                            <td className="px-4 py-4 text-sm text-gray-600">{apt.lead_email}</td>
                            <td className="px-4 py-4 text-sm text-gray-600">{apt.lead_phone}</td>
                            <td className="px-4 py-4 text-sm text-gray-600">
                              {aptDate.toLocaleDateString('fr-BE', { 
                                weekday: 'short',
                                day: '2-digit', 
                                month: 'short', 
                                year: 'numeric' 
                              })}
                            </td>
                            <td className="px-4 py-4 text-sm font-semibold text-gray-900">{apt.appointment_time}</td>
                            <td className="px-4 py-4 text-sm">
                              {isPast ? (
                                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-200 text-gray-700">
                                  Passé
                                </span>
                              ) : (
                                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                                  À venir
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-4 text-sm text-gray-600">{formatDate(apt.created_at)}</td>
                          </tr>
                        );
                      })
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-4 text-sm text-gray-600">
              Total : {appointments.length} rendez-vous
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============== LEGAL PAGES ==============

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Politique de Confidentialité</h1>
        <p className="text-sm text-gray-600 mb-8">Dernière mise à jour : {new Date().toLocaleDateString('fr-BE')}</p>
        
        <div className="space-y-6 text-gray-700">
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Identité du Responsable du Traitement</h2>
            <p><strong>DMCA Consulting</strong></p>
            <p>Rue Josaphat 61, 1030 Schaerbeek, Belgique</p>
            <p>Email : afofana@dmcaconsulting.com</p>
            <p>Téléphone : 0497 23 31 63</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">2. Données Collectées</h2>
            <p>Nous collectons les données suivantes lorsque vous utilisez notre site :</p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>Nom et prénom</li>
              <li>Adresse email</li>
              <li>Numéro de téléphone</li>
              <li>Informations fiscales (revenus annuels, montant des impôts)</li>
              <li>Profession et statut professionnel</li>
              <li>Données de navigation (cookies, adresse IP)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">3. Finalités du Traitement</h2>
            <p>Vos données sont utilisées pour :</p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>Qualifier votre demande de consultation fiscale</li>
              <li>Vous contacter pour planifier un rendez-vous</li>
              <li>Vous fournir des conseils fiscaux personnalisés</li>
              <li>Améliorer nos services</li>
              <li>Respecter nos obligations légales</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Base Légale</h2>
            <p>Le traitement de vos données repose sur :</p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>Votre consentement explicite</li>
              <li>L'exécution d'un contrat de service</li>
              <li>Le respect d'obligations légales (conformité FSMA)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Durée de Conservation</h2>
            <p>Vos données sont conservées pendant :</p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>3 ans maximum après votre dernier contact avec nous</li>
              <li>10 ans pour les données comptables (obligation légale belge)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Vos Droits (RGPD)</h2>
            <p>Conformément au Règlement Général sur la Protection des Données (RGPD), vous disposez des droits suivants :</p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li><strong>Droit d'accès</strong> : Obtenir une copie de vos données</li>
              <li><strong>Droit de rectification</strong> : Corriger vos données inexactes</li>
              <li><strong>Droit à l'effacement</strong> : Supprimer vos données</li>
              <li><strong>Droit à la limitation</strong> : Limiter le traitement</li>
              <li><strong>Droit à la portabilité</strong> : Recevoir vos données dans un format structuré</li>
              <li><strong>Droit d'opposition</strong> : Vous opposer au traitement</li>
            </ul>
            <p className="mt-3">Pour exercer ces droits, contactez-nous à : <a href="mailto:afofana@dmcaconsulting.com" className="text-blue-600 hover:underline">afofana@dmcaconsulting.com</a></p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Sécurité des Données</h2>
            <p>Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos données contre tout accès non autorisé, perte ou destruction.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">8. Cookies</h2>
            <p>Notre site utilise des cookies pour améliorer votre expérience et analyser le trafic. Vous pouvez gérer vos préférences de cookies via les paramètres de votre navigateur.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">9. Transferts de Données</h2>
            <p>Vos données sont hébergées en Europe et ne sont pas transférées hors de l'Espace Économique Européen (EEE).</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">10. Réclamation</h2>
            <p>Si vous estimez que vos droits ne sont pas respectés, vous pouvez introduire une réclamation auprès de l'Autorité de Protection des Données belge (APD) :</p>
            <p className="mt-2">
              <strong>Autorité de Protection des Données</strong><br />
              Rue de la Presse 35, 1000 Bruxelles<br />
              Website : <a href="https://www.autoriteprotectiondonnees.be" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">www.autoriteprotectiondonnees.be</a>
            </p>
          </section>
        </div>

        <div className="mt-10 pt-6 border-t">
          <Link to="/" className="text-blue-600 hover:text-blue-700 font-medium">
            ← Retour à l'accueil
          </Link>
        </div>
      </div>
    </div>
  );
};

const LegalNotice = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Mentions Légales</h1>
        
        <div className="space-y-6 text-gray-700">
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Éditeur du Site</h2>
            <p><strong>Raison sociale :</strong> DMCA Consulting</p>
            <p><strong>Adresse :</strong> Rue Josaphat 61, 1030 Schaerbeek, Belgique</p>
            <p><strong>Email :</strong> <a href="mailto:afofana@dmcaconsulting.com" className="text-blue-600 hover:underline">afofana@dmcaconsulting.com</a></p>
            <p><strong>Téléphone :</strong> 0497 23 31 63</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">2. Directeur de Publication</h2>
            <p>Le directeur de la publication du site est le représentant légal de DMCA Consulting.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">3. Hébergement</h2>
            <p><strong>Hébergeur :</strong> Emergent AI</p>
            <p>Le site est hébergé sur une infrastructure cloud sécurisée.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Propriété Intellectuelle</h2>
            <p>L'ensemble du contenu de ce site (textes, images, vidéos, logos) est la propriété exclusive de DMCA Consulting, sauf mention contraire.</p>
            <p className="mt-2">Toute reproduction, distribution ou utilisation sans autorisation préalable est strictement interdite et constitue une contrefaçon sanctionnée par le Code de la propriété intellectuelle.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Limitation de Responsabilité</h2>
            <p>DMCA Consulting s'efforce d'assurer l'exactitude des informations publiées sur ce site. Toutefois, nous ne pouvons garantir l'exhaustivité, la précision ou l'actualité des informations.</p>
            <p className="mt-2">Les informations fournies le sont à titre indicatif et ne constituent pas des conseils fiscaux personnalisés. Pour toute décision fiscale, il est recommandé de consulter un professionnel.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Conformité FSMA</h2>
            <p>Nos services de conseil fiscal sont fournis dans le respect de la réglementation belge et sous la supervision de la FSMA (Autorité des Services et Marchés Financiers).</p>
            <p className="mt-2"><strong>Important :</strong> Nous ne promettons aucun résultat spécifique en matière de réduction d'impôts. Chaque situation est unique et nécessite une analyse personnalisée.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Liens Externes</h2>
            <p>Ce site peut contenir des liens vers des sites externes. DMCA Consulting n'est pas responsable du contenu de ces sites tiers.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">8. Droit Applicable</h2>
            <p>Le présent site est soumis au droit belge. Tout litige relatif à l'utilisation du site sera de la compétence exclusive des tribunaux belges.</p>
          </section>
        </div>

        <div className="mt-10 pt-6 border-t">
          <Link to="/" className="text-blue-600 hover:text-blue-700 font-medium">
            ← Retour à l'accueil
          </Link>
        </div>
      </div>
    </div>
  );
};

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Conditions Générales d'Utilisation</h1>
        <p className="text-sm text-gray-600 mb-8">Dernière mise à jour : {new Date().toLocaleDateString('fr-BE')}</p>
        
        <div className="space-y-6 text-gray-700">
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Objet</h2>
            <p>Les présentes Conditions Générales d'Utilisation (CGU) régissent l'utilisation du site web de DMCA Consulting et les services de conseil fiscal proposés.</p>
            <p className="mt-2">En utilisant ce site, vous acceptez sans réserve les présentes CGU.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">2. Services Proposés</h2>
            <p>DMCA Consulting propose les services suivants :</p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>Consultation fiscale gratuite (analyse initiale)</li>
              <li>Optimisation fiscale personnalisée</li>
              <li>Préparation de la pension</li>
              <li>Conseils pour professionnels de santé</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">3. Accès au Site</h2>
            <p>L'accès au site est gratuit. Les frais de connexion et d'équipement nécessaires sont à la charge de l'utilisateur.</p>
            <p className="mt-2">DMCA Consulting se réserve le droit de suspendre ou d'interrompre l'accès au site à tout moment, sans préavis, pour des raisons de maintenance ou de mise à jour.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Inscription et Formulaire de Contact</h2>
            <p>Pour bénéficier d'une consultation, vous devez remplir le formulaire de qualification en fournissant des informations exactes et complètes.</p>
            <p className="mt-2">Vous vous engagez à :</p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>Fournir des informations véridiques</li>
              <li>Mettre à jour vos informations si nécessaire</li>
              <li>Ne pas usurper l'identité d'autrui</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Obligations de l'Utilisateur</h2>
            <p>L'utilisateur s'engage à :</p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>Utiliser le site de manière légale et conforme</li>
              <li>Ne pas tenter de perturber le fonctionnement du site</li>
              <li>Ne pas collecter de données personnelles d'autres utilisateurs</li>
              <li>Respecter les droits de propriété intellectuelle</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Responsabilité de DMCA Consulting</h2>
            <p>DMCA Consulting s'engage à fournir des conseils fiscaux de qualité. Toutefois :</p>
            <ul className="list-disc ml-6 mt-2 space-y-1">
              <li>Nous ne garantissons aucun résultat spécifique en matière de réduction d'impôts</li>
              <li>Chaque situation fiscale est unique et nécessite une analyse personnalisée</li>
              <li>Les conseils fournis le sont dans le respect de la législation belge en vigueur</li>
            </ul>
            <p className="mt-3"><strong>Avertissement FSMA :</strong> Les informations fournies ne constituent pas des conseils en investissement. Pour toute décision financière importante, consultez un professionnel agréé.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Confidentialité</h2>
            <p>Vos données personnelles sont traitées conformément à notre <Link to="/politique-confidentialite" className="text-blue-600 hover:underline">Politique de Confidentialité</Link>.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">8. Tarification</h2>
            <p>La première consultation (analyse de qualification) est gratuite. Les services de conseil personnalisé feront l'objet d'un devis sur mesure qui vous sera communiqué lors de l'entretien initial.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">9. Modification des CGU</h2>
            <p>DMCA Consulting se réserve le droit de modifier les présentes CGU à tout moment. Les nouvelles conditions seront applicables dès leur mise en ligne.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">10. Droit Applicable et Juridiction</h2>
            <p>Les présentes CGU sont soumises au droit belge. En cas de litige, les tribunaux de Bruxelles seront seuls compétents.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">11. Contact</h2>
            <p>Pour toute question relative aux présentes CGU, vous pouvez nous contacter :</p>
            <p className="mt-2">
              <strong>DMCA Consulting</strong><br />
              Rue Josaphat 61, 1030 Schaerbeek, Belgique<br />
              Email : <a href="mailto:afofana@dmcaconsulting.com" className="text-blue-600 hover:underline">afofana@dmcaconsulting.com</a><br />
              Téléphone : 0497 23 31 63
            </p>
          </section>
        </div>

        <div className="mt-10 pt-6 border-t">
          <Link to="/" className="text-blue-600 hover:text-blue-700 font-medium">
            ← Retour à l'accueil
          </Link>
        </div>
      </div>
    </div>
  );
};

// ============== MAIN APP ==============
function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/qualified" element={<QualifiedPage />} />
            <Route path="/not-qualified" element={<NotQualifiedPage />} />
            <Route path="/book-appointment" element={<BookAppointment />} />
            <Route path="/confirmation" element={<ConfirmationPage />} />
            <Route path="/professionnels-sante" element={<HealthcareProfessionals />} />
            <Route path="/qualified-healthcare" element={<QualifiedHealthcarePage />} />
            <Route path="/blog" element={<BlogList />} />
            <Route path="/blog/:slug" element={<BlogArticle />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/politique-confidentialite" element={<PrivacyPolicy />} />
            <Route path="/mentions-legales" element={<LegalNotice />} />
            <Route path="/conditions-utilisation" element={<TermsOfService />} />
            <Route 
              path="/admin" 
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              } 
            />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
